import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import '../Markets.css';

const Stocks: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    }
  };

  const sectors = [
    { name: 'Technology', examples: 'Apple, Microsoft, Google, Amazon, Meta', icon: '💻' },
    { name: 'Healthcare', examples: 'Johnson & Johnson, Pfizer, UnitedHealth', icon: '🏥' },
    { name: 'Financials', examples: 'JPMorgan, Bank of America, Visa', icon: '🏦' },
    { name: 'Consumer Discretionary', examples: 'Tesla, Nike, McDonald\'s, Starbucks', icon: '🛍️' },
    { name: 'Energy', examples: 'ExxonMobil, Chevron, Shell', icon: '⚡' },
    { name: 'Industrials', examples: 'Boeing, Caterpillar, 3M', icon: '🏭' },
    { name: 'Telecommunications', examples: 'Verizon, AT&T, T-Mobile', icon: '📱' },
    { name: 'Consumer Staples', examples: 'Coca-Cola, Procter & Gamble, Walmart', icon: '🛒' }
  ];

  const popularStocks = [
    { ticker: 'AAPL', name: 'Apple Inc.', sector: 'Technology' },
    { ticker: 'MSFT', name: 'Microsoft Corporation', sector: 'Technology' },
    { ticker: 'GOOGL', name: 'Alphabet Inc.', sector: 'Technology' },
    { ticker: 'AMZN', name: 'Amazon.com Inc.', sector: 'Consumer Discretionary' },
    { ticker: 'TSLA', name: 'Tesla Inc.', sector: 'Automotive/Technology' },
    { ticker: 'META', name: 'Meta Platforms Inc.', sector: 'Technology' },
    { ticker: 'NVDA', name: 'NVIDIA Corporation', sector: 'Technology' },
    { ticker: 'JPM', name: 'JPMorgan Chase & Co.', sector: 'Financials' },
    { ticker: 'V', name: 'Visa Inc.', sector: 'Financials' },
    { ticker: 'JNJ', name: 'Johnson & Johnson', sector: 'Healthcare' },
    { ticker: 'WMT', name: 'Walmart Inc.', sector: 'Consumer Staples' },
    { ticker: 'PG', name: 'Procter & Gamble', sector: 'Consumer Staples' }
  ];

  return (
    <motion.div 
      className="markets-page"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <div className="page-content">
        <motion.div className="page-title" variants={itemVariants}>
          <h1>Stock Trading</h1>
          <p>Trade individual stocks from global markets</p>
        </motion.div>

        

        <motion.section className="market-overview" variants={itemVariants}>
          <h2>What is Stock Trading?</h2>
          <p>
            Stock trading involves buying and selling shares of publicly traded companies. When you purchase a stock, you're buying a small 
            piece of ownership in that company. As the company grows and becomes more profitable, the value of your shares can increase, 
            providing potential capital gains and dividend income.
          </p>
          <p>
            Modern stock trading through CFDs (Contracts for Difference) allows you to speculate on price movements without owning the 
            underlying shares, providing access to leverage, short-selling opportunities, and the ability to profit in both rising and falling markets.
          </p>
        </motion.section>

        <motion.section className="market-features" variants={itemVariants}>
          <h2>Why Trade Stocks?</h2>
          <div className="features-grid">
            <motion.div className="feature" variants={itemVariants} whileHover={{ scale: 1.05 }}>
              <h3>Ownership & Dividends</h3>
              <p>Own part of successful companies and receive dividend payments</p>
            </motion.div>
            
            <motion.div className="feature" variants={itemVariants} whileHover={{ scale: 1.05 }}>
              <h3>Growth Potential</h3>
              <p>Benefit from company expansion and innovation</p>
            </motion.div>
            
            <motion.div className="feature" variants={itemVariants} whileHover={{ scale: 1.05 }}>
              <h3>Transparency</h3>
              <p>Public companies disclose financial statements and operations</p>
            </motion.div>
            
            <motion.div className="feature" variants={itemVariants} whileHover={{ scale: 1.05 }}>
              <h3>Liquidity</h3>
              <p>Major stocks can be bought and sold quickly at market prices</p>
            </motion.div>
            
            <motion.div className="feature" variants={itemVariants} whileHover={{ scale: 1.05 }}>
              <h3>Sector Selection</h3>
              <p>Choose companies from industries you understand and believe in</p>
            </motion.div>
            
            <motion.div className="feature" variants={itemVariants} whileHover={{ scale: 1.05 }}>
              <h3>Leverage with CFDs</h3>
              <p>Control larger positions with smaller capital through CFD trading</p>
            </motion.div>
          </div>
        </motion.section>

        <motion.section className="sectors-section" variants={itemVariants}>
          <h2>Major Stock Sectors</h2>
          <div className="sectors-grid">
            {sectors.map((sector, i) => (
              <motion.div key={i} className="sector-card" variants={itemVariants} whileHover={{ scale: 1.03 }}>
                <div className="sector-icon">{sector.icon}</div>
                <h3>{sector.name}</h3>
                <p className="examples">{sector.examples}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        <motion.section className="popular-stocks" variants={itemVariants}>
          <h2>Popular Stocks to Trade</h2>
          <div className="stocks-grid">
            {popularStocks.map((stock, i) => (
              <motion.div key={i} className="stock-card" variants={itemVariants} whileHover={{ scale: 1.03 }}>
                <h3 className="ticker">{stock.ticker}</h3>
                <p className="company-name">{stock.name}</p>
                <p className="sector-tag">{stock.sector}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        <motion.section className="trading-approaches" variants={itemVariants}>
          <h2>Stock Trading Approaches</h2>
          <div className="approaches-grid">
            <div className="approach">
              <h3>📊 Fundamental Analysis</h3>
              <p>Evaluate company financials, earnings, P/E ratios, and growth prospects</p>
              <ul>
                <li>Revenue and profit growth</li>
                <li>Earnings per share (EPS)</li>
                <li>Price-to-earnings (P/E) ratio</li>
                <li>Debt levels and cash flow</li>
              </ul>
            </div>
            <div className="approach">
              <h3>📈 Technical Analysis</h3>
              <p>Study price charts, patterns, and indicators to predict movements</p>
              <ul>
                <li>Support and resistance levels</li>
                <li>Moving averages and trends</li>
                <li>Volume and momentum indicators</li>
                <li>Chart patterns (head & shoulders, triangles)</li>
              </ul>
            </div>
            <div className="approach">
              <h3>💡 Growth Investing</h3>
              <p>Focus on companies with above-average growth potential</p>
              <ul>
                <li>High revenue growth rates</li>
                <li>Innovative products or services</li>
                <li>Expanding market share</li>
                <li>Disruptive technology</li>
              </ul>
            </div>
            <div className="approach">
              <h3>💰 Value Investing</h3>
              <p>Find undervalued stocks trading below intrinsic value</p>
              <ul>
                <li>Low P/E and P/B ratios</li>
                <li>Strong fundamentals</li>
                <li>Dividend-paying companies</li>
                <li>Temporary market pessimism</li>
              </ul>
            </div>
          </div>
        </motion.section>

        <motion.section className="market-factors" variants={itemVariants}>
          <h2>Factors Affecting Stock Prices</h2>
          <div className="factors-grid">
            <div className="factor">
              <h4>📈 Earnings Reports</h4>
              <p>Quarterly earnings, revenue, and forward guidance</p>
            </div>
            <div className="factor">
              <h4>📰 Company News</h4>
              <p>Product launches, acquisitions, management changes</p>
            </div>
            <div className="factor">
              <h4>🏦 Economic Data</h4>
              <p>GDP, employment, interest rates, inflation</p>
            </div>
            <div className="factor">
              <h4>🏭 Industry Trends</h4>
              <p>Sector-specific developments and competition</p>
            </div>
            <div className="factor">
              <h4>🌍 Market Sentiment</h4>
              <p>Investor confidence, fear, and greed indices</p>
            </div>
            <div className="factor">
              <h4>⚖️ Regulations</h4>
              <p>Government policies and legal changes</p>
            </div>
          </div>
        </motion.section>

        <motion.section className="trading-strategies" variants={itemVariants}>
          <h2>Stock Trading Strategies</h2>
          <div className="strategies-grid">
            <div className="strategy">
              <h3>📊 Day Trading</h3>
              <p>Buy and sell within the same trading day to capture intraday moves</p>
            </div>
            <div className="strategy">
              <h3>🔄 Swing Trading</h3>
              <p>Hold positions for several days or weeks to profit from price swings</p>
            </div>
            <div className="strategy">
              <h3>💎 Position Trading</h3>
              <p>Long-term approach holding stocks for months or years</p>
            </div>
            <div className="strategy">
              <h3>⚡ Momentum Trading</h3>
              <p>Trade stocks showing strong directional movement and volume</p>
            </div>
            <div className="strategy">
              <h3>📰 News Trading</h3>
              <p>Capitalize on price movements triggered by earnings and announcements</p>
            </div>
            <div className="strategy">
              <h3>💹 Pair Trading</h3>
              <p>Trade correlated stocks by going long one and short another</p>
            </div>
          </div>
        </motion.section>

        <motion.section className="trading-tips" variants={itemVariants}>
          <h2>Stock Trading Tips</h2>
          <ul className="tips-list">
            <li>Research companies thoroughly before investing—understand their business model</li>
            <li>Monitor earnings calendars and be cautious trading around earnings reports</li>
            <li>Diversify across different sectors to reduce portfolio risk</li>
            <li>Use stop-loss orders to protect against significant losses</li>
            <li>Pay attention to market opening and closing hours for best liquidity</li>
            <li>Watch for stock splits, dividends, and corporate actions</li>
            <li>Consider both technical and fundamental factors in your analysis</li>
            <li>Stay updated on industry news and competitive landscape</li>
            <li>Avoid emotional trading—stick to your strategy and risk management rules</li>
            <li>Start with blue-chip stocks before trading smaller, volatile companies</li>
          </ul>
        </motion.section>

        <motion.div className="cta-section" variants={itemVariants}>
          <h2>Start Trading Stocks Today</h2>
          <p>Access professional stock trading signals and comprehensive market analysis</p>
          <div className="btn-container">
            <Link to="/services/signals" className="btn">Get Trading Signals</Link>
            <Link to="/education" className="btn secondary">Learn More</Link>
            <Link to="/ib-partners" className="btn secondary">Choose Your Broker</Link>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Stocks;
